import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * ✅ Modern, minimalist invoice PDF for ZUBAI DESIGN
 * - Uses public images:
 *   - /images/zubaide.png
 *   - /images/QR_zubaide.jpg
 *
 * NOTE: Async because we fetch + convert images to DataURL.
 */
export async function generateInvoicePdf({ invoiceNo, customer, lines, notes }) {
    // ---------- Helpers ----------
    const money = (n) => `RM ${Number(n || 0).toFixed(2)}`;

    // Tukar prefix ZB -> ZD sahaja (bahagian belakang kekal)
    const normalizeInvoiceNo = (no) => {
        const s = String(no || "").trim();
        if (!s) return "ZD-0000";
        if (s.startsWith("ZB")) return "ZD" + s.slice(2);
        return s; // kalau dah ZD atau format lain, biar
    };

    // Fetch image from public URL -> DataURL (jsPDF addImage perlukan DataURL)
    const loadImageAsDataURL = async (url) => {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Failed to load image: ${url}`);
        const blob = await res.blob();

        return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = reject;
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });
    };

    // ---------- Config (tweak sini) ----------
    const BRAND_NAME = "ZUBAI DESIGN";
    const BRAND_REG = "202303292433";

    const CONTACT_PHONE = "+60192702700";
    const CONTACT_EMAIL = "zubaidesign@gmail.com";
    const CONTACT_WEB = "zubaide.com";

    // Ayat deposit (English) - tweak wording sini
    const DEPOSIT_TEXT =
        "To confirm this project, a 50% deposit of the total amount is required upon acceptance of this invoice.";

    // Ayat akaun (English) - ikut arahan kau
    const BANK_TEXT =
        "Payment to be made to ZUBAI DESIGN, RHB Bank No. 26601600050200.";

    // Path images dalam folder public
    // Jika file kau bukan dalam /public/images, tukar URL kat sini.
    const LOGO_URL = "/images/zubaide.png";
    const QR_URL = "/images/QR_zubaide.jpg";

    // QR placement config (tweak mudah)
    const QR_W = 40;
    const QR_H = 42;

    const M = 14;
    const RESERVED_BOTTOM = 18;

    // ---------- Create doc ----------
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    // Try load images (kalau gagal, PDF still jalan tanpa gambar)
    let logoDataUrl = null;
    let qrDataUrl = null;

    try {
        logoDataUrl = await loadImageAsDataURL(LOGO_URL);
    } catch (e) {
        console.warn("Logo not loaded:", e.message);
    }

    try {
        qrDataUrl = await loadImageAsDataURL(QR_URL);
    } catch (e) {
        console.warn("QR not loaded:", e.message);
    }

    const invNo = normalizeInvoiceNo(invoiceNo);
    const dateStr = new Date().toLocaleDateString("ms-MY");

    // ---------- Header (modern) ----------
    // Logo kiri
    const logoSize = 14; // tweak size logo
    if (logoDataUrl) {
        // PNG → "PNG"
        doc.addImage(logoDataUrl, "PNG", M, M - 2, logoSize, logoSize);
    }

    // Brand name + reg (kiri atas)
    const brandX = logoDataUrl ? M + logoSize + 6 : M;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(BRAND_NAME, brandX, M + 4);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(90);
    doc.text(BRAND_REG, brandX, M + 9);
    doc.setTextColor(0);

    // "INVOICE" kanan (besar, clean)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("INVOICE", pageW - M, M + 6, { align: "right" });

    // Meta invoice (kanan bawah INVOICE)
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(60);
    doc.text(`Invoice No: ${invNo}`, pageW - M, M + 13, { align: "right" });
    doc.text(`Date: ${dateStr}`, pageW - M, M + 18, { align: "right" });
    doc.setTextColor(0);

    // Garis halus bawah header
    doc.setDrawColor(230);
    doc.line(M, M + 22, pageW - M, M + 22);



    // ---------- Bill To block ----------
    const topY = M + 30;

    // Nama utama: company kalau ada, kalau tiada guna nama customer
    const displayName = customer?.company || customer?.name || "-";

    // ===== Main recipient =====
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(30);
    doc.text(displayName, M, topY + 7);

    // ===== Customer Details =====
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(80);

    let metaY = topY + 13;

    // Jika ada company, nama orang letak sebagai Attn
    if (customer?.company && customer?.name) {
        doc.text(`Attn: ${customer.name}`, M, metaY);
        metaY += 4.5;
    }

    if (customer?.address) {
        const addressLines = doc.splitTextToSize(
            String(customer.address),
            82
        );

        doc.text(addressLines, M, metaY);
        metaY += addressLines.length * 4.2;
    }

    if (customer?.phone) {
        doc.text(customer.phone, M, metaY);
        metaY += 4.5;
    }

    if (customer?.email) {
        doc.text(customer.email, M, metaY);
        metaY += 4.5;
    }

    // Reset to default for next sections
    doc.setTextColor(0);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    // ===== Customer Details (secondary, CONSISTENT) =====
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(80);





    // Reset to default for next sections
    doc.setTextColor(0);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);



    doc.setTextColor(0);

    // Deposit note (kanan, modern callout)
    const noteBoxW = 86; // tweak lebar box
    const noteBoxX = pageW - M - noteBoxW;
    const noteBoxY = topY - 2;

    doc.setDrawColor(235);
    doc.setFillColor(250, 250, 250);
    doc.roundedRect(noteBoxX, noteBoxY, noteBoxW, 20, 3, 3, "FD");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(40);
    doc.text("PAYMENT TERMS", noteBoxX + 6, noteBoxY + 7);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(90);
    const depositLines = doc.splitTextToSize(DEPOSIT_TEXT, noteBoxW - 12);
    doc.text(depositLines, noteBoxX + 6, noteBoxY + 12);
    doc.setTextColor(0);

    // ---------- Table ----------
    const ITEM_COL_W = pageW - M - M - 32 - 18 - 32 - 12;

    const body = (lines || []).map((l) => {
        const qty = Number(l.qty || 0);
        const price = Number(l.price || 0);
        const total = qty * price;

        const note = String(l.note || "").trim();
        const noteLines = note ? doc.splitTextToSize(note, ITEM_COL_W) : [];

        return [
            {
                content: l.name || "-",
                note,
                noteLines
            },
            money(price),
            String(qty),
            money(total)
        ];
    });

    autoTable(doc, {
        startY: Math.max(topY + 30, metaY + 8),
        head: [["Item", "Price", "Qty", "Total"]],

        body,

        // ✅ Minimalist styling
        theme: "plain",
        styles: {
            font: "helvetica",
            fontSize: 10,
            cellPadding: { top: 4, right: 3, bottom: 4, left: 3 },
            textColor: 30,
        },
        headStyles: {
            textColor: 20,
            fontStyle: "bold",
        },
        columnStyles: {
            0: { cellWidth: "auto" },   // Item
            1: { cellWidth: 32, halign: "right" }, // Price
            2: { cellWidth: 18, halign: "center" }, // Qty
            3: { cellWidth: 32, halign: "right" }  // Total
        },


        // Garis halus bawah row (modern)
        didParseCell: (data) => {
            data.cell.styles.lineWidth = 0.2;
            data.cell.styles.lineColor = 235;
            data.cell.styles.valign = "top";

            if (
                data.section === "body" &&
                data.column.index === 0 &&
                data.cell.raw?.noteLines?.length
            ) {
                data.cell.styles.minCellHeight =
                    9 + data.cell.raw.noteLines.length * 3.4;
            }
        },

        didDrawCell: (data) => {
            if (
                data.section === "body" &&
                data.column.index === 0 &&
                data.cell.raw?.noteLines?.length
            ) {
                doc.setFont("helvetica", "normal");
                doc.setFontSize(7.5);
                doc.setTextColor(120);

                doc.text(
                    data.cell.raw.noteLines,
                    data.cell.x + 3,
                    data.cell.y + 10
                );

                doc.setTextColor(0);
                doc.setFontSize(10);
            }
        },

        // ✅ Biar table respect ruang QR di bawah
        margin: { left: M, right: M, bottom: RESERVED_BOTTOM },
    });

    // ---------- Totals + Notes (di last page) ----------
    const sum = (lines || []).reduce(
        (a, b) => a + Number(b.qty || 0) * Number(b.price || 0),
        0
    );

    // Pastikan kita lukis total & footer di page terakhir
    const lastPage = doc.getNumberOfPages();
    doc.setPage(lastPage);

    let y = doc.lastAutoTable.finalY + 10;

    // Kalau ruang tak cukup (akan langgar footer/QR), tambah page baru
    const safeBottomY = pageH - RESERVED_BOTTOM - 8;
    if (y > safeBottomY) {
        doc.addPage();
        y = M;
    }

    // Total block kanan
    const totalBoxW = 70;
    const totalBoxX = pageW - M - totalBoxW;

    doc.setDrawColor(235);
    doc.setFillColor(252, 252, 252);
    doc.roundedRect(totalBoxX, y, totalBoxW, 18, 3, 3, "FD");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(80);
    doc.text("Grand Total", totalBoxX + 6, y + 7);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(20);
    doc.text(money(sum), totalBoxX + 6, y + 14);
    doc.setTextColor(0);

    // Notes (optional) — kiri, wrap nicely
    if (notes) {
        const notesX = M;
        const notesW = totalBoxX - M - 8;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(40);
        doc.text("Notes", notesX, y + 4);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(90);
        const noteLines = doc.splitTextToSize(String(notes), notesW);
        doc.text(noteLines, notesX, y + 9);
        doc.setTextColor(0);
    }

    // ---------- Footer / Payment area (bottom) ----------
    // QR at bottom-left (align bawah + margin kiri)
    const qrX = M; // “margin sikit sebelah kiri”
    const qrY = pageH - M - QR_H; // start from bottom (align bawah)
    if (qrDataUrl) {
        // JPG → "JPEG"
        doc.addImage(qrDataUrl, "JPEG", qrX, qrY, QR_W, QR_H);
    } else {
        // placeholder kalau QR tak load
        doc.setDrawColor(230);
        doc.rect(qrX, qrY, QR_W, QR_H);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text("QR not found", qrX + 4, qrY + 8);
        doc.setTextColor(0);
    }

    // Payment instructions (kanan QR)
    const payX = qrX + QR_W + 10;
    const payY = qrY + 6;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(30);
    doc.text("PAYMENT", payX, payY);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(90);

    const payText = doc.splitTextToSize(BANK_TEXT, pageW - M - payX);
    doc.text(payText, payX, payY + 6);

    // Contact line
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(60);
    doc.text(
        `${CONTACT_PHONE}  •  ${CONTACT_EMAIL}  •  ${CONTACT_WEB}`,
        payX,
        pageH - M - 6
    );

    doc.setTextColor(0);

    // ---------- Save ----------
    doc.save(`invoice-${invNo}.pdf`);
}

// ================================
// ✅ COMPACT RECEIPT PDF
// ================================
export async function generateReceiptPdf({
    invoiceNo,
    customer,
    lines,
    notes,
    total,
    paidAt,
    paymentMethod,
    paymentRef
}) {
    const money = (n) => `RM ${Number(n || 0).toFixed(2)}`;

    const normalizeInvoiceNo = (no) => {
        const s = String(no || "").trim();
        if (!s) return "ZD-0000";
        if (s.startsWith("ZB")) return "ZD" + s.slice(2);
        return s;
    };

    const loadImageAsDataURL = async (url) => {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Failed to load image: ${url}`);
        const blob = await res.blob();

        return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = reject;
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });
    };

    const BRAND_NAME = "ZUBAI DESIGN";
    const BRAND_REG = "202303292433";

    const CONTACT_PHONE = "+60192702700";
    const CONTACT_EMAIL = "zubaidesign@gmail.com";
    const CONTACT_WEB = "zubaide.com";

    const LOGO_URL = "/images/zubaide.png";

    const M = 14;
    const doc = new jsPDF({ unit: "mm", format: "a4" });

    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    let logoDataUrl = null;

    try {
        logoDataUrl = await loadImageAsDataURL(LOGO_URL);
    } catch (e) {
        console.warn("Logo not loaded:", e.message);
    }

    const invNo = normalizeInvoiceNo(invoiceNo);
    const receiptNo = `RCPT-${invNo}`;

    const paidDateStr = paidAt
        ? new Date(paidAt).toLocaleDateString("ms-MY")
        : new Date().toLocaleDateString("ms-MY");

    const paidAmount =
        Number(total) ||
        (lines || []).reduce(
            (sum, line) =>
                sum + Number(line.qty || 0) * Number(line.price || 0),
            0
        );

    // ================================
    // Header
    // ================================
    const logoSize = 12;

    if (logoDataUrl) {
        doc.addImage(logoDataUrl, "PNG", M, M - 1, logoSize, logoSize);
    }

    const brandX = logoDataUrl ? M + logoSize + 6 : M;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(20);
    doc.text(BRAND_NAME, brandX, M + 4);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(90);
    doc.text(BRAND_REG, brandX, M + 8.5);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(21);
    doc.setTextColor(20);
    doc.text("RECEIPT", pageW - M, M + 5, { align: "right" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(70);
    doc.text(`Receipt No: ${receiptNo}`, pageW - M, M + 11, {
        align: "right"
    });
    doc.text(`Invoice No: ${invNo}`, pageW - M, M + 15.5, {
        align: "right"
    });

    doc.setDrawColor(230);
    doc.line(M, M + 21, pageW - M, M + 21);

    // ================================
    // Customer + Paid Badge
    // ================================
    const topY = M + 31;
    const displayName = customer?.company || customer?.name || "-";

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(90);
    doc.text("RECEIVED FROM", M, topY);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(30);
    doc.text(displayName, M, topY + 7);

    let metaY = topY + 13;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(80);

    if (customer?.company && customer?.name) {
        doc.text(`Attn: ${customer.name}`, M, metaY);
        metaY += 4;
    }

    if (customer?.phone) {
        doc.text(customer.phone, M, metaY);
        metaY += 4;
    }

    if (customer?.email) {
        doc.text(customer.email, M, metaY);
        metaY += 4;
    }

    // Paid badge kanan, kecil
    const badgeW = 34;
    const badgeH = 11;
    const badgeX = pageW - M - badgeW;
    const badgeY = topY - 2;

    doc.setFillColor(232, 245, 233);
    doc.setDrawColor(168, 213, 172);
    doc.roundedRect(badgeX, badgeY, badgeW, badgeH, 3, 3, "FD");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(34, 120, 60);
    doc.text("PAID", badgeX + badgeW / 2, badgeY + 7.5, {
        align: "center"
    });

    // ================================
    // Payment Summary compact
    // ================================
    const boxY = Math.max(metaY + 8, topY + 30);
    const boxW = pageW - M * 2;
    const boxH = 34;

    doc.setFillColor(250, 250, 250);
    doc.setDrawColor(235);
    doc.roundedRect(M, boxY, boxW, boxH, 3, 3, "FD");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(40);
    doc.text("PAYMENT SUMMARY", M + 6, boxY + 8);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(90);

    doc.text("Description", M + 6, boxY + 17);
    doc.text("Paid Date", M + 6, boxY + 24);
    doc.text("Payment Method", M + 6, boxY + 31);

    doc.setTextColor(30);
    doc.text(`Payment for Invoice ${invNo}`, M + 43, boxY + 17);
    doc.text(paidDateStr, M + 43, boxY + 24);
    doc.text(paymentMethod || "-", M + 43, boxY + 31);

    if (paymentRef) {
        doc.setTextColor(90);
        doc.text("Payment Ref", pageW - M - 67, boxY + 31);

        doc.setTextColor(30);
        doc.text(String(paymentRef), pageW - M - 32, boxY + 31);
    }

    // ================================
    // Amount Paid
    // ================================
    const amountY = boxY + boxH + 14;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(80);
    doc.text("Amount Paid", M, amountY);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(20);
    doc.text(money(paidAmount), M, amountY + 11);

    doc.setDrawColor(230);
    doc.line(M, amountY + 18, pageW - M, amountY + 18);

    // ================================
    // Items table - compact
    // ================================
    const tableBody = (lines || []).map((line) => {
        const qty = Number(line.qty || 0);
        const price = Number(line.price || 0);

        return [
            line.name || "-",
            money(price),
            String(qty),
            money(qty * price)
        ];
    });

    autoTable(doc, {
        startY: amountY + 27,
        head: [["Item", "Price", "Qty", "Total"]],
        body: tableBody,
        theme: "grid",
        styles: {
            font: "helvetica",
            fontSize: 8,
            cellPadding: { top: 2.4, right: 2.5, bottom: 2.4, left: 2.5 },
            textColor: 35,
            lineWidth: 0.15,
            lineColor: 230,
            overflow: "linebreak"
        },
        headStyles: {
            fontStyle: "bold",
            textColor: 20,
            fillColor: [250, 250, 250],
            lineColor: 230,
            lineWidth: 0.15
        },
        columnStyles: {
            0: { cellWidth: 104 },
            1: { halign: "right", cellWidth: 30 },
            2: { halign: "center", cellWidth: 18 },
            3: { halign: "right", cellWidth: 30 }
        },
        margin: { left: M, right: M, bottom: 22 },
        pageBreak: "avoid"
    });

    // ================================
    // Notes / Footer
    // ================================
    let y = doc.lastAutoTable.finalY + 8;

    if (notes && y < pageH - 36) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(40);
        doc.text("Notes", M, y);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(90);

        const noteLines = doc.splitTextToSize(String(notes), pageW - M * 2);
        doc.text(noteLines, M, y + 5);
    }

    // Footer fixed, tiada QR supaya receipt muat 1 page
    doc.setDrawColor(235);
    doc.line(M, pageH - 24, pageW - M, pageH - 24);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(110);
    doc.text(
        "This is a computer-generated receipt. No signature is required.",
        M,
        pageH - 17
    );

    doc.setFontSize(8.5);
    doc.setTextColor(60);
    doc.text(
        `${CONTACT_PHONE}  •  ${CONTACT_EMAIL}  •  ${CONTACT_WEB}`,
        M,
        pageH - 11
    );

    doc.save(`receipt-${invNo}.pdf`);
}